import { isEquipmentItem } from "common";
import { groupBy } from "lodash";
import { ExperiencePurchaseItem, Guest, GuestStatus } from "types";
import { getDemographicLineItems } from "@/models/PurchaseItem";

// Equipment experiences with "Collect demographic information" off carry no per-attendee guests, so
// check-in status lives on the item (purchaseItem.guestStatus) instead of guests[]. Treat such an item
// as a single check-in unit whose status is the item-level guestStatus.
export const isItemLevelCheckIn = (purchaseItem: ExperiencePurchaseItem) =>
    isEquipmentItem(purchaseItem) && (purchaseItem.guests?.length ?? 0) === 0;

export const getGuestsByStatus = (purchaseItem: ExperiencePurchaseItem) => {
    const guestStatus = [GuestStatus.ARRIVED, GuestStatus.PENDING, GuestStatus.NO_SHOW, GuestStatus.RETURNED];
    const guestStatusMap: any = {};

    if (isItemLevelCheckIn(purchaseItem)) {
        // Represent the item as one unit under its own guestStatus.
        for (const status of guestStatus) {
            guestStatusMap[status] = purchaseItem.guestStatus === (status as unknown) ? 1 : 0;
        }
        return guestStatusMap;
    }

    const groupedGuests = purchaseItem?.guests ? groupBy(purchaseItem.guests, "guestStatus") : {};
    for (const status of guestStatus) {
        guestStatusMap[status] = (groupedGuests[status] ?? []).length;
    }

    return guestStatusMap;
};

export const getCheckInStatusInfo = (purchaseItem: ExperiencePurchaseItem, guest?: Guest) => {
    // For item-level check-in (no guests) the item counts as a single unit.
    const totalGuestsCount = isItemLevelCheckIn(purchaseItem) ? 1 : purchaseItem.guests.length;
    const guestStatusMap: { [key in GuestStatus]: number } = getGuestsByStatus(purchaseItem);

    return {
        areAllGuestsNoShow: guestStatusMap[GuestStatus.NO_SHOW] === totalGuestsCount,
        areAllGuestsCheckedIn: guestStatusMap[GuestStatus.ARRIVED] === totalGuestsCount,
        isCheckedIn: guest
            ? guest?.guestStatus === GuestStatus.ARRIVED
            : guestStatusMap[GuestStatus.ARRIVED] === totalGuestsCount,
        isReturned: guest
            ? guest?.guestStatus === GuestStatus.RETURNED
            : guestStatusMap[GuestStatus.RETURNED] === totalGuestsCount,
        isNoShow: guest
            ? guest?.guestStatus === GuestStatus.NO_SHOW
            : guestStatusMap[GuestStatus.NO_SHOW] === totalGuestsCount,
        areGuestsCheckedInOrReturnedOnly:
            guestStatusMap[GuestStatus.ARRIVED] + guestStatusMap[GuestStatus.RETURNED] === totalGuestsCount,
    };
};

export interface GuestSummaryByStatus {
    [status: string]: {
        name: string;
        quantity: number;
    }[];
}

export const getDemographicNamesGroupedByGuestStatus = (purchaseItem: ExperiencePurchaseItem): GuestSummaryByStatus => {
    const result: GuestSummaryByStatus = {};

    const demographicsMap: { [id: string]: string } = {};
    for (const demographic of getDemographicLineItems(purchaseItem)) {
        demographicsMap[demographic.template.id] = demographic.name;
    }

    for (const guest of purchaseItem.guests) {
        const status = guest.guestStatus || GuestStatus.PENDING;
        const templateId = guest.template?.id;
        const type = templateId ? demographicsMap[templateId] : "";

        if (!result[status]) {
            result[status] = [];
        }

        const existingGuest = result[status].find((guest) => guest.name === type);
        if (existingGuest) {
            existingGuest.quantity += 1;
        } else {
            result[status].push({ name: type, quantity: 1 });
        }
    }

    return result;
};

export const getSummarizedGuestsData = (items: ExperiencePurchaseItem[]): GuestSummaryByStatus => {
    const summary: Record<string, Map<string, { name: string; quantity: number }>> = {};

    for (const item of items) {
        const grouped = getDemographicNamesGroupedByGuestStatus(item);

        for (const [status, guests] of Object.entries(grouped)) {
            if (!summary[status]) {
                summary[status] = new Map();
            }

            const map = summary[status];

            for (const guest of guests) {
                const existing = map.get(guest.name);

                if (existing) {
                    existing.quantity += guest.quantity;
                } else {
                    map.set(guest.name, { ...guest });
                }
            }
        }
    }

    const result: GuestSummaryByStatus = {};
    for (const [status, map] of Object.entries(summary)) {
        result[status] = [...map.values()];
    }

    return result;
};
